import { useState, useMemo } from 'react';

import {
  ChecklistPopover,
  Divider,
  Flex,
  Form,
  Header,
  PrimaryButton,
  SortableTags,
  Spacer,
  SubText,
  TertiaryButton,
  type IChecklistPopoverProps,
  type ISortableTagContentProps,
  type ISortableTagsProps,
  type SelectValue,
} from '@procurify/ui'
import { type FormikHelpers } from 'formik'
import produce from 'immer'
import { useIntl } from 'react-intl'
import * as Yup from 'yup'

import { type ExporterColumn } from '@webapp/models'
import messages from './messages'
import { ExporterFormatFormStyled } from './styles/ExporterFormatFormStyled'
import { type ExportFormat } from '../../types'
import { ExporterColumnTextField } from '../ExporterColumnTextField'

export interface IExporterFormatFormProps {
  columns: ExporterColumn[]
  initialData?: {
    name?: ExportFormat['name']
    date_format?: ExportFormat['date_format']
    decimal_point?: ExportFormat['decimal_point']
  }
  onCancel?: () => void
  onSubmit?: (
    data: ExporterFormatFormSchema & { columns: ExporterColumn[] },
    actions: FormikHelpers<
      ExporterFormatFormSchema & { columns: ExporterColumn[] }
    >
  ) => void
}

export interface ExporterFormatFormSchema {
  name: string
  date_format: SelectValue
  decimal_point: SelectValue
}

const dateFormatOptions = [
  {
    label: 'MM-DD-YYYY',
    value: '%m-%d-%Y',
  },
  {
    label: 'DD-MM-YYYY',
    value: '%d-%m-%Y',
  },
  {
    label: 'YYYY-MM-DD',
    value: '%Y-%m-%d',
  },
  {
    label: 'MM/DD/YYYY',
    value: '%m/%d/%Y',
  },
  {
    label: 'DD/MM/YYYY',
    value: '%d/%m/%Y',
  },
  {
    label: 'YYYY/MM/DD',
    value: '%Y/%m/%d',
  },
]

export const ExporterFormatForm = ({
  columns = [],
  initialData = {},
  onCancel = () => undefined,
  onSubmit = () => undefined,
}: IExporterFormatFormProps) => {
  const intl = useIntl()

  const [columnsList, setColumnsList] = useState(columns)

  const filteredTags = columnsList
    .filter((column) => {
      return column.active
    })
    .map((column) => ({
      value: column,
    }))

  const formSchema = Yup.object<ExporterFormatFormSchema>({
    name: Yup.string().required(
      intl.formatMessage(messages.formatNameRequiredError)
    ),
    date_format: Yup.object<SelectValue>({
      label: Yup.string().required(),
      value: Yup.string().required(),
    }),
    decimal_point: Yup.object<SelectValue>({
      label: Yup.string().required(),
      value: Yup.string().required(),
    }),
  })

  const decimalOptions = [
    {
      label: intl.formatMessage(messages.periodOptionLabel),
      value: 'true',
    },
    {
      label: intl.formatMessage(messages.commaOptionLabel),
      value: 'false',
    },
  ]

  const formattedInitialData = {
    name: initialData.name || '',
    decimal_point:
      typeof initialData.decimal_point === 'boolean'
        ? decimalOptions.find(
            (opt) => opt.value === initialData.decimal_point.toString()
          ) || decimalOptions[0]
        : decimalOptions[0],
    date_format: initialData.date_format
      ? dateFormatOptions.find(
          (opt) => opt.value === initialData.date_format
        ) || dateFormatOptions[0]
      : dateFormatOptions[0],
  }

  const TagContent = useMemo(
    () =>
      ({ innerProps: { value } }: ISortableTagContentProps<ExporterColumn>) => (
        <ExporterColumnTextField
          value={value.header}
          onChange={(e) => {
            const changeValue = e.target.value
            setColumnsList((state) =>
              produce(state, (draft) => {
                const updatedColumn = draft.find((col) => col.key === value.key)

                updatedColumn.header = changeValue
              })
            )
          }}
        />
      ),
    []
  )

  const onTagSort: ISortableTagsProps['handleTagReorder'] = (
    newTags,
    oldIndex,
    newIndex
  ) => {
    setColumnsList((state) =>
      produce(state, (draft) => {
        const item = draft.splice(oldIndex, 1)[0]

        draft.splice(newIndex, 0, item)
      })
    )
  }

  const onChecklistUpdate: IChecklistPopoverProps['onUpdate'] = (
    updatedItems
  ) => {
    setColumnsList((state) =>
      produce(state, (draft) => {
        updatedItems.forEach((updatedItem) => {
          const item = draft.find((column) => column.key === updatedItem.value)

          if (item) {
            item.active = updatedItem.checked
          }
        })
      })
    )
  }

  const onFormSubmit = (values, actions) => {
    onSubmit(
      {
        ...values,
        columns: columnsList.filter((column) => {
          return column.active
        }),
      },
      actions
    )
  }

  return (
    <ExporterFormatFormStyled>
      <Form
        onSubmit={onFormSubmit}
        validationSchema={formSchema}
        initialValues={formattedInitialData}
      >
        {({ submitForm, isSubmitting }) => (
          <>
            <Flex.Row flexWrap='nowrap'>
              <Flex.Item flexGrow={3}>
                <Form.TextField
                  id='name'
                  name='name'
                  label={intl.formatMessage(messages.formatNameLabel)}
                  placeholder={intl.formatMessage(
                    messages.formatNamePlaceholder
                  )}
                />
              </Flex.Item>
              <Flex.Item flexGrow={1}>
                <Form.Select
                  id='date_format'
                  name='date_format'
                  label={intl.formatMessage(messages.dateFormatLabel)}
                  defaultValue={dateFormatOptions[0]}
                  options={dateFormatOptions}
                />
              </Flex.Item>
              <Flex.Item flexGrow={1}>
                <Form.Select
                  id='decimal_point'
                  name='decimal_point'
                  label={intl.formatMessage(messages.decimalSymbolLabel)}
                  defaultValue={decimalOptions[0]}
                  options={decimalOptions}
                />
              </Flex.Item>
            </Flex.Row>

            <Divider space={1.5} />

            <Flex.Row flexWrap='nowrap' alignItems='center'>
              <Flex.Item flexGrow={1}>
                <Header as='h5'>
                  {intl.formatMessage(messages.columnOrgTitle)}
                </Header>
                <SubText>{intl.formatMessage(messages.columnOrgDesc)}</SubText>
              </Flex.Item>
              <Flex.Item>
                <ChecklistPopover
                  onUpdate={onChecklistUpdate}
                  checklist={columnsList.map((col) => ({
                    label: col.header,
                    value: col.key,
                    checked: col.active,
                  }))}
                />
              </Flex.Item>
            </Flex.Row>

            <Spacer />

            <div className='column-sort-container'>
              <SortableTags<ExporterColumn>
                tags={filteredTags}
                components={{ TagContent }}
                handleTagReorder={onTagSort}
              />
            </div>

            <Divider space={1.5} />

            <Flex.Row>
              <Flex.Item ml='auto'>
                <TertiaryButton
                  type='submit'
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  {intl.formatMessage(messages.cancelButtonLabel)}
                </TertiaryButton>
              </Flex.Item>
              <Flex.Item>
                <PrimaryButton
                  type='submit'
                  onClick={submitForm}
                  isLoading={isSubmitting}
                >
                  {initialData.name
                    ? intl.formatMessage(messages.editButtonLabel)
                    : intl.formatMessage(messages.saveButtonLabel)}
                </PrimaryButton>
              </Flex.Item>
            </Flex.Row>
          </>
        )}
      </Form>
    </ExporterFormatFormStyled>
  )
}

ExporterFormatForm.displayName = 'ExporterFormatForm'
